import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SideDrawer from '../../components/SideDrawer'
import CopyIcon from '../../common/CopyIcon'
import { wdDecide, wdGet, wdSettle, wdSettlePreview, serverMessage, userAccessList } from './api'
import {
  fmtKg, fmtInr, fmtDate, dimsText, deadlineLabel, ageLabel, decisionsFor, decisionBlocker, settlementBounds, TONE, WD_STATUS,
} from './format'
import { WdStatusChip, HoldChip, ToneChip } from './shared/Chips'
import KeyValue, { Section } from './shared/KeyValue'

const cellSx = { fontSize: 12, py: 0.75, px: 1, borderBottom: '1px solid #f1f5f9' }
const headSx = { ...cellSx, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#888', fontWeight: 700 }

/** Thumbnails that open the full image in a new tab. Signed S3 links, so never cached here. */
/** The modules the server accepts for "courier accepted" (WdActionAccessRules; T18). */
const COURIER_ACCEPT_MODULES = ['WEIGHTS_TEAM', 'SA']

/** The merchant's packing video as a frame with a play mark, beside their photos; it plays in the dialog. */
function VideoThumb({ src, onPlay }) {
  return (
    <Box
      component='button'
      type='button'
      onClick={onPlay}
      aria-label='Play the packing video'
      sx={{ position: 'relative', p: 0, width: 96, height: 96, borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb', bgcolor: '#0b1220', cursor: 'pointer', flexShrink: 0 }}
    >
      <Box component='video' src={src} preload='metadata' muted playsInline sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      <PlayCircleOutlineIcon sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: 34, color: '#fff', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.6))' }} />
    </Box>
  )
}

function ImageStrip({ links, empty }) {
  const list = (links || []).filter(Boolean)
  if (!list.length) return <Typography sx={{ fontSize: 12, color: '#6b7280' }}>{empty}</Typography>
  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {list.map((src, i) => (
        <Box
          key={`${i}-${src.slice(-24)}`}
          component='a'
          href={src}
          target='_blank'
          rel='noreferrer'
          sx={{ display: 'block', width: 96, height: 96, borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb', bgcolor: '#f8fafc' }}
        >
          <Box component='img' src={src} alt={`evidence ${i + 1}`} loading='lazy' sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </Box>
      ))}
    </Box>
  )
}

/**
 * The three claims about one parcel side by side: what the merchant declared,
 * what Proship pushed to the courier, what the courier billed. A reviewer's
 * decision is almost always a reading of this table, so every figure a row can
 * carry is here and nothing is derived that the row does not hold.
 */
function WeightCompare({ row }) {
  const over = row.appliedWeight != null && row.originalWeight != null && row.appliedWeight > row.originalWeight
  const lines = [
    ['Chargeable weight', fmtKg(row.originalWeight), fmtKg(row.courierSentKg), fmtKg(row.appliedWeight), true],
    ['Dead weight', row.claimedDeadKg != null ? `${fmtKg(row.claimedDeadKg)} (scale reading)` : '—', '—', fmtKg(row.deadKg)],
    ['Volumetric', fmtKg(row.declaredVolKg), '—', fmtKg(row.volKg)],
    ['Carton (L × B × H)', dimsText(row.declaredDims), '—', dimsText(row.dims)],
  ]
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size='small' sx={{ minWidth: 480 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headSx} />
            <TableCell sx={headSx}>Merchant declared</TableCell>
            <TableCell sx={headSx}>Pushed to courier</TableCell>
            <TableCell sx={headSx}>Courier billed</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map(([label, a, b, c, strong]) => (
            <TableRow key={label}>
              <TableCell sx={{ ...cellSx, color: '#6b7280' }}>{label}</TableCell>
              <TableCell sx={{ ...cellSx, fontWeight: strong ? 700 : 500 }}>{a}</TableCell>
              <TableCell sx={{ ...cellSx, fontWeight: strong ? 700 : 500 }}>{b}</TableCell>
              <TableCell sx={{ ...cellSx, fontWeight: strong ? 700 : 500, color: strong && over ? TONE.danger.fg : 'inherit' }}>{c}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography sx={{ fontSize: 12, mt: 1, color: '#374151' }}>
        Additional freight claimed: <b>+{fmtKg(row.excessKg)}</b> → <b>{fmtInr(row.excessAmount)}</b>
        {row.holdStatus === 'HELD' && row.holdAmount != null ? ` (of which ${fmtInr(row.holdAmount)} is on hold on the merchant's wallet)` : ''}
      </Typography>
    </Box>
  )
}

function StatusLog({ log }) {
  const list = log || []
  if (!list.length) return <Typography sx={{ fontSize: 12, color: '#6b7280' }}>No status changes recorded.</Typography>
  return (
    <Stack spacing={1}>
      {list.map((e, i) => (
        <Box key={`${e.status}-${e.at}-${i}`} sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1.25, alignItems: 'start' }}>
          <WdStatusChip status={e.status} />
          <Box>
            <Typography sx={{ fontSize: 12, color: '#1a1a2e' }}>
              {e.by || 'System'} <Box component='span' sx={{ color: '#9ca3af' }}>· {fmtDate(e.at)}</Box>
            </Typography>
            {e.remark && <Typography sx={{ fontSize: 12, color: '#4b5563' }}>{e.remark}</Typography>}
          </Box>
        </Box>
      ))}
    </Stack>
  )
}

/**
 * Everything a reviewer needs to decide one dispute, and the decision itself.
 * Rendered keyed on the row id by the drawer shell below, so a new row is a
 * fresh mount with a clean form — no reset effect to get wrong.
 *
 * The decision posts to the same v1 endpoint the old screen used — that endpoint
 * is the only thing that moves status and money (and, with the hold model on,
 * releases or converts the wallet hold) — so this drawer adds clarity, not a
 * second money path.
 */
export function DisputeDrawerBody({ row, onClose, onDecided, merchantName }) {
  const { enqueueSnackbar } = useSnackbar()
  const [decision, setDecision] = useState('')
  const [remark, setRemark] = useState('')
  const [busy, setBusy] = useState(false)
  // The packing video plays in a dialog. Opened as a link it downloaded instead: the stored file
  // carries no video content type, which a <video> element tolerates and a browser tab does not.
  const [videoOpen, setVideoOpen] = useState(false)
  // Item 3 — the settlement weight (kg, typed) and what the server says it would cost.
  const [weightKg, setWeightKg] = useState('')
  const [preview, setPreview] = useState(null)
  const [previewErr, setPreviewErr] = useState('')
  const [previewing, setPreviewing] = useState(false)

  const options = useMemo(() => decisionsFor(row), [row])
  // "Courier accepted" is refused by the server without one of these modules; say so here instead.
  const canCourierAccept = useMemo(() => {
    const access = userAccessList() || []
    return COURIER_ACCEPT_MODULES.some((m) => access.includes(m))
  }, [])
  const chosen = options.find((o) => o.value === decision) || null
  const bounds = useMemo(() => settlementBounds(row), [row])
  const blocker = decisionBlocker(chosen, { remark, weightKg, row })

  // Price the candidate weight on the server as the reviewer types it (debounced), so the
  // figures they confirm are the figures the rate card will charge — never a client guess.
  useEffect(() => {
    setPreview(null)
    setPreviewErr('')
    if (!row || !chosen || !chosen.needsWeight) return undefined
    if (decisionBlocker(chosen, { remark: 'x', weightKg, row })) return undefined   // weight itself not valid yet
    let alive = true
    const timer = setTimeout(() => {
      setPreviewing(true)
      wdSettlePreview(row.id, Math.round(Number(weightKg) * 1000))
        .then((p) => { if (alive) setPreview(p) })
        .catch((err) => { if (alive) setPreviewErr(serverMessage(err, 'Could not price this weight')) })
        .finally(() => { if (alive) setPreviewing(false) })
    }, 350)
    return () => { alive = false; clearTimeout(timer) }
  }, [row, chosen, weightKg])

  const submit = async () => {
    if (!row || !chosen || blocker) return
    setBusy(true)
    try {
      if (chosen.needsWeight) {
        const res = await wdSettle(row.id, Math.round(Number(weightKg) * 1000), remark.trim())
        if (!res || res.ok === false) {
          enqueueSnackbar((res && res.reason) || 'The server did not apply this settlement', { variant: 'error' })
          return
        }
        enqueueSnackbar(`${row.awb}: settled at ${fmtKg(weightKg)}${preview ? ` — merchant pays ${fmtInr(preview.merchantPays)}` : ''}`, { variant: 'success' })
        if (onDecided) onDecided(res.row || null)
        return
      }
      const res = await wdDecide(row.awb, chosen.value, remark.trim() || `${chosen.label} (admin, Weight v2)`)
      if (!res || res.actionResult === false) {
        enqueueSnackbar((res && res.reason) || 'The server did not apply this decision', { variant: 'error' })
        return
      }
      enqueueSnackbar(`${row.awb}: ${chosen.label}`, { variant: 'success' })
      let fresh = null
      try { fresh = await wdGet(row.id) } catch (e) { /* the list reload still reflects it */ }
      if (onDecided) onDecided(fresh)
    } catch (err) {
      enqueueSnackbar(serverMessage(err, 'Decision failed'), { variant: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const deadline = row.status === 'NEW' ? deadlineLabel(row.deadlineAt) : null
  const merchant = merchantName || row.merchantName || ''
  const settled = row.status === 'SETTLED_PROSHIP' && row.settledKg != null

  return (
    <Box sx={{ width: '100%', display: 'grid', gap: 2 }}>
      <Stack direction='row' spacing={1} flexWrap='wrap' rowGap={1} alignItems='center'>
        <WdStatusChip status={row.status} />
        <HoldChip holdStatus={row.holdStatus} holdAmount={row.holdAmount} />
        {deadline && <ToneChip tone={deadline.tone} label={`Auto-accept: ${deadline.text}`} />}
        {row.shipmentState && <ToneChip tone='grey' label={row.shipmentState === 'DELIVERED' ? 'Delivered' : 'In transit'} />}
        <ToneChip tone='danger' label={`+${fmtKg(row.excessKg)} · ${fmtInr(row.excessAmount)}`} title='Additional freight the courier billed' />
        {settled && (
          <ToneChip
            tone='warning'
            label={`Settled at ${fmtKg(row.settledKg)} · merchant paid ${fmtInr(row.settledCharge)}${row.settledReleased ? ` · ${fmtInr(row.settledReleased)} released` : ''}`}
            title="Item 3: priced from the merchant's own rate card at the settled weight"
          />
        )}
      </Stack>

      <Section title='Shipment'>
        <KeyValue
          columns={3}
          items={[
            { label: 'Merchant', value: merchant || row.merchantId },
            { label: 'Order reference', value: row.orderId },
            { label: 'Courier · service', value: [row.courier, row.service].filter(Boolean).join(' · ') },
            { label: 'SKU', value: row.sku || (row.product ? '(multi-SKU or no code)' : null) },
            { label: 'Product', value: row.product },
            { label: 'Raised on', value: fmtDate(row.createdAt) },
            { label: 'Merchant deadline', value: fmtDate(row.deadlineAt) },
            { label: 'Last update', value: fmtDate(row.updatedAt) },
            { label: 'Product category (merchant)', value: row.productCategory },
          ]}
        />
      </Section>

      <Section title='Weights — who says what'>
        <WeightCompare row={row} />
      </Section>

      <Section
        title="Merchant's evidence"
        right={(row.videoLink || row.productUrl) && (
          <Stack direction='row' spacing={1}>
            {row.videoLink && (
              <Button size='small' variant='outlined' onClick={() => setVideoOpen(true)} startIcon={<PlayCircleOutlineIcon />} sx={{ fontSize: 11 }}>
                Packing video
              </Button>
            )}
            {row.productUrl && (
              <Button size='small' variant='text' href={row.productUrl} target='_blank' rel='noreferrer' endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />} sx={{ fontSize: 11 }}>
                Product page
              </Button>
            )}
          </Stack>
        )}
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {row.videoLink && <VideoThumb src={row.videoLink} onPlay={() => setVideoOpen(true)} />}
        <ImageStrip
          links={row.merchantImageLinks}
          empty={row.hasVideo
            ? 'No photographs — the merchant attached a video only.'
            : 'Nothing uploaded by the merchant. A merchant cannot dispute without dimensions, a dead weight and at least one photo or video, so an open dispute with nothing here predates v2.'}
        />
        </Box>
      </Section>

      <Section title="Courier's proof">
        <ImageStrip links={row.courierImageLinks} empty="No proof images from the courier on this row." />
      </Section>

      <Section title='History'>
        <StatusLog log={row.statusLog} />
      </Section>

      {options.length ? (
        <Section title='Decision'>
          <RadioGroup value={decision} onChange={(e) => setDecision(e.target.value)}>
            <Stack spacing={1}>
              {options.map((o) => {
                const c = TONE[o.tone] || TONE.grey
                const active = decision === o.value
                const locked = o.value === 'ACCEPTED_BY_COURIER' && !canCourierAccept
                return (
                  <Box
                    key={o.value}
                    sx={{
                      border: `1px solid ${active ? c.fg : '#e5e7eb'}`, borderRadius: '8px', px: 1.25, py: 0.5,
                      bgcolor: active ? c.bg : 'transparent',
                    }}
                  >
                    <FormControlLabel
                      value={o.value}
                      disabled={locked}
                      control={<Radio size='small' />}
                      sx={{ m: 0, alignItems: 'flex-start', width: '100%' }}
                      label={(
                        <Box sx={{ py: 0.5 }}>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#1a1a2e' }}>{o.label}</Typography>
                          <Typography sx={{ fontSize: 11.5, color: '#4b5563' }}>{o.help}</Typography>
                          {locked && (
                            <Typography sx={{ fontSize: 11.5, color: '#b45309', mt: 0.25 }}>
                              Needs the WEIGHTS_TEAM or SA access module, which your login does not have.
                            </Typography>
                          )}
                        </Box>
                      )}
                    />
                  </Box>
                )
              })}
            </Stack>
          </RadioGroup>
          {chosen && chosen.needsWeight && (
            <Stack direction='row' spacing={1.5} alignItems='flex-start' flexWrap='wrap' rowGap={1} sx={{ mt: 1.5 }}>
              <TextField
                label='Settled weight (kg)'
                type='number'
                size='small'
                value={weightKg}
                onChange={(e) => setWeightKg(e.target.value)}
                inputProps={{ step: 0.01, min: bounds ? bounds.minKg : 0, max: bounds ? bounds.maxKg : undefined }}
                helperText={bounds ? `Strictly between ${fmtKg(bounds.minKg)} (declared) and ${fmtKg(bounds.maxKg)} (courier billed)` : 'No weight gap on this row'}
                sx={{ width: 260 }}
              />
              <Typography sx={{ fontSize: 12, color: previewErr ? TONE.danger.fg : '#374151', pt: 1, flex: '1 1 260px' }}>
                {previewing
                  ? 'Pricing on the merchant’s rate card…'
                  : previewErr
                    || (preview
                      ? `Merchant pays ${fmtInr(preview.merchantPays)} of the ${fmtInr(preview.fullClaim)} claimed (${fmtInr(preview.originalAmount)} → ${fmtInr(preview.amountAtWeight)} at ${fmtKg(preview.settledKg)}); ${fmtInr(preview.released)} of the ${fmtInr(preview.holdAmount)} on hold goes back to the merchant.`
                      : 'Enter a weight to see what the rate card charges at it.')}
              </Typography>
            </Stack>
          )}
          <TextField
            fullWidth
            multiline
            minRows={2}
            size='small'
            label={chosen && chosen.remarkRequired ? 'Remark (required — goes on the record and to the merchant)' : 'Remark (goes on the record and to the merchant)'}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            sx={{ mt: 1.5 }}
          />
          {chosen && chosen.restrictedTo && (
            <Typography sx={{ fontSize: 11.5, color: '#6b7280', mt: 0.5 }}>
              Restricted to the {chosen.restrictedTo}; the server refuses other logins.
            </Typography>
          )}
          <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 1.5 }}>
            <LoadingButton
              variant='contained'
              loading={busy}
              disabled={!chosen || !!blocker || (chosen.needsWeight && (!preview || previewing))}
              onClick={submit}
              sx={{ fontSize: 12, fontWeight: 700 }}
              color={chosen && chosen.tone === 'danger' ? 'error' : 'primary'}
            >
              {chosen ? `Confirm: ${chosen.label}` : 'Choose a decision'}
            </LoadingButton>
            <Button onClick={onClose} sx={{ fontSize: 12 }}>Close</Button>
            {chosen && blocker && <Typography sx={{ fontSize: 11.5, color: '#6b7280' }}>{blocker}</Typography>}
          </Stack>
        </Section>
      ) : (
        <Alert severity='info' sx={{ fontSize: 12 }}>
          This dispute is closed ({(WD_STATUS[row.status] || {}).label || row.status}). No further decision is possible here.
        </Alert>
      )}
      <Dialog open={videoOpen} onClose={() => setVideoOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle sx={{ fontSize: 14, fontWeight: 600 }}>Packing video · AWB {row.awb}</DialogTitle>
        <DialogContent>
          {videoOpen && row.videoLink && (
            <Box component='video' src={row.videoLink} controls autoPlay playsInline
              sx={{ display: 'block', width: '100%', maxHeight: '70vh', bgcolor: '#0b1220', borderRadius: 1 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button size='small' onClick={() => setVideoOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

/** The drawer shell: header + a body keyed on the row, so every row opens on a clean form. */
export default function DisputeDrawer({ row, open, onClose, onDecided, merchantName }) {
  const merchant = merchantName || (row && row.merchantName) || ''
  return (
    <SideDrawer
      open={!!(open && row)}
      setOpen={(v) => { if (!v) onClose() }}
      drawerWidth='58%'
      icon={<GavelOutlinedIcon />}
      title={row ? (
        <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          AWB {row.awb}<CopyIcon text={row.awb} />
        </Box>
      ) : ''}
      subtitle={row ? [merchant, row.courier, row.createdAt ? `raised ${ageLabel(row.createdAt)}` : null].filter(Boolean).join(' · ') : ''}
    >
      {row && <DisputeDrawerBody key={row.id} row={row} onClose={onClose} onDecided={onDecided} merchantName={merchant} />}
    </SideDrawer>
  )
}
